import { useEffect, useState } from "react";
import { sortTeams } from "@/lib/ageOrder";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPicker } from "@/components/app/UserPicker";

const L = ({ children }) => <Label className="text-[10px] tracking-widest uppercase opacity-70">{children}</Label>;
const Btn = ({ onClick, testId, children, disabled, ghost }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} className={ghost ? "px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase" : "btn-volt px-5 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40"}>{children}</button>
);

export function KitDialog({ kit, onClose, onSaved }) {
  const [form, setForm] = useState({ number: "", location: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (kit) setForm({ number: kit.number || "", location: kit.location || "", notes: kit.notes || "" }); }, [kit]);
  const save = async () => {
    if (!form.number.trim()) { toast.error("Kit number is required"); return; }
    setBusy(true);
    try { if (kit?.id) await api.put(`/admin/first-aid/kits/${kit.id}`, form); else await api.post("/admin/first-aid/kits", form); toast.success("Kit saved"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!kit} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="fak-kit-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{kit?.id ? `Edit ${kit.number}` : "Register a kit"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><L>Kit number (unique)</L><Input value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} placeholder="FAK-001" data-testid="fak-form-number" /></div>
          <div><L>Location / stored at</L><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Clubhouse store, Pitch 2 bag…" data-testid="fak-form-location" /></div>
          <div><L>Notes</L><Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="fak-form-notes" /></div>
          <div className="flex justify-end gap-2 pt-1"><Btn ghost onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy} testId="fak-form-save">{busy ? "Saving…" : "Save"}</Btn></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AssignDialog({ kit, users, teams, onClose, onSaved }) {
  const [userId, setUserId] = useState(null);
  const [teamId, setTeamId] = useState("none");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (kit) { setUserId(kit.assigned_user_id || null); setTeamId(kit.team_id || "none"); } }, [kit]);
  const save = async () => {
    if (!userId) { toast.error("Pick a coach"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/admin/first-aid/kits/${kit.id}/assign`, { user_id: userId, team_id: teamId === "none" ? null : teamId }); toast.success("Kit assigned — the coach has been notified"); onSaved(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!kit} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="fak-assign-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Assign {kit?.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><L>Coach</L><UserPicker users={users} value={userId} onChange={setUserId} testId="fak-assign-user" /></div>
          <div><L>Team (optional)</L>
            <Select value={teamId} onValueChange={setTeamId}><SelectTrigger data-testid="fak-assign-team"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">— none —</SelectItem>{sortTeams(teams).map(t => <SelectItem key={t.id} value={t.id}>{t.age_group ? `${t.age_group} · ` : ""}{t.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <p className="text-xs opacity-60">The coach gets a push + email and must tap Accept once they've collected the kit.</p>
          <div className="flex justify-end gap-2 pt-1"><Btn ghost onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy} testId="fak-assign-save">{busy ? "Assigning…" : "Assign"}</Btn></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ChecklistDialog({ open, items, onClose, onSaved }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setList(items.map(i => ({ ...i }))); }, [open, items]);
  const upd = (i, k, v) => setList(l => l.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const save = async () => {
    const clean = list.filter(i => i.label.trim()).map(i => ({ key: (i.key || i.label).toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40), label: i.label.trim(), target: Number(i.target) || 0 }));
    setBusy(true);
    try { const { data } = await api.put("/admin/first-aid/checklist", { items: clean }); toast.success("Checklist saved"); onSaved(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const reset = async () => { try { const { data } = await api.post("/admin/first-aid/checklist/reset"); toast.success("Reset to default"); onSaved(data); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="fak-checklist-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Checklist template</DialogTitle></DialogHeader>
        <p className="text-xs opacity-60">Items every kit should contain and the target quantity. Coaches record what's actually in their kit against this list.</p>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {list.map((i, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input value={i.label} onChange={e => upd(idx, "label", e.target.value)} placeholder="Item" data-testid={`fak-cl-label-${idx}`} className="flex-1 h-9" />
              <Input type="number" min={0} value={i.target} onChange={e => upd(idx, "target", e.target.value)} data-testid={`fak-cl-target-${idx}`} className="w-20 h-9" />
              <button type="button" onClick={() => setList(l => l.filter((_, j) => j !== idx))} className="p-1.5 rounded border border-border hover:border-red-500 hover:text-red-400" aria-label="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap justify-between gap-2 pt-1">
          <div className="flex gap-2">
            <Btn ghost onClick={() => setList(l => [...l, { key: "", label: "", target: 1 }])} testId="fak-cl-add"><Plus className="w-3 h-3 inline mr-1" />Add item</Btn>
            <Btn ghost onClick={reset} testId="fak-cl-reset">Reset to default</Btn>
          </div>
          <div className="flex gap-2"><Btn ghost onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={busy} testId="fak-cl-save">{busy ? "Saving…" : "Save"}</Btn></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
